import React, { useState, useRef, useEffect } from 'react';
import {
    Button,
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Spinner
} from 'reactstrap';
import {
    IconButton,
    Tooltip
} from '@mui/material';
import { AutoStories as AutoStoriesIcon } from '@mui/icons-material';
import { getBlob, post } from '../../components/hooks/apiFetch';
import {
    downloadEdited,
    fetchFullText,
    updateText,
    uploadNewFileVersion
} from './services/textService';
import useSpellCheck from './hooks/useSpellCheck';
import ConfirmDialog from '../ConfirmDialog/ConfirmDialog';
import { TextEditor } from './components/editors/TextEditor';
import { TxtViewer } from './components/viewers/TxtViewer';
import { MarkdownViewer } from './components/viewers/MarkdownViewer';
import { PDFViewer } from './components/viewers/PdfViewer';
import { DocxViewer } from './components/viewers/DocxViewer';
import { checkMarkIcon } from '../../assets/icons';
import { getFileType, isBinaryFormat } from './services/fileTypeService';
import './DocumentPreviewModal.css';
import { useNotification } from '../../components/Notification/NotificationContext';

const MAX_FILE_SIZE = 1024 * 1024 * 1024; // 1 ГБ

const ACCEPT_BY_TYPE = {
    pdf: '.pdf,application/pdf',
    docx: '.docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document'
};

const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} Б`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} КБ`;
    if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} МБ`;
    return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} ГБ`;
};

export const DocumentPreviewModal = ({
    id,
    inputIdentifier,
    fileName,
    version,
    baseFileId,
    downloadUrl,
    onFileUpdated,
    record,
    canEdit,
    currentUserAccess,
    sendFileVersionNotifications
}) => {
    const [modal, setModal] = useState(false);
    const [fullText, setFullText] = useState('');
    const [fileBlob, setFileBlob] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [isEditing, setIsEditing] = useState(false);
    const [saving, setSaving] = useState(false);
    const [copyAllSuccess, setCopyAllSuccess] = useState(false);
    const [hasChanges, setHasChanges] = useState(false);
    const [hasMarkedAsRead, setHasMarkedAsRead] = useState(false);
    const [pdfObjectUrl, setPdfObjectUrl] = useState(null);
    const [fontSize, setFontSize] = useState(() => {
        const saved = localStorage.getItem('fontSize');
        return saved ? Number(saved) : 16;
    });
    const [lineHeight, setLineHeight] = useState(() => {
        const saved = localStorage.getItem('lineHeight');
        return saved ? Number(saved) : 1.5;
    });
    const [confirmDialog, setConfirmDialog] = useState({
        show: false,
        message: '',
        onConfirm: null,
        onCancel: null
    });

    const {
        showSuccess,
        showError,
        showWarning
    } = useNotification();

    const editorRef = useRef(null);
    const fileInputRef = useRef(null);
    const fileType = getFileType(fileName);
    const isBinary = isBinaryFormat(fileType);
    const canUploadNewVersion = canEdit && isBinary && !isEditing;
    const isEditable = !isBinary && canEdit;

    useEffect(() => {
        localStorage.setItem('fontSize', fontSize);
    }, [fontSize]);

    useEffect(() => {
        localStorage.setItem('lineHeight', lineHeight);
    }, [lineHeight]);

    useEffect(() => {
        return () => {
            if (pdfObjectUrl) {
                URL.revokeObjectURL(pdfObjectUrl);
            }
        };
    }, [pdfObjectUrl]);

    const { check: checkSpelling } = useSpellCheck();

    const markAsRead = async () => {
        if (!record?.isNew || hasMarkedAsRead) return;

        try {
            await post('/api/FileChanges/MarkAsRead', [record.readChangesId]);
            setHasMarkedAsRead(true);

            if (onFileUpdated) {
                await onFileUpdated('markRead');
            }
        } catch (error) {
            console.error('Ошибка при снятии метки "Новое":', error);
        }
    };

    const loadFileContent = async () => {
        setLoading(true);
        setError(null);
        try {
            if (fileType === 'txt' || fileType === 'md') {
                const text = await fetchFullText(inputIdentifier, fileName, version, baseFileId);
                setFullText(text);
            } else {
                const blob = await getBlob(downloadUrl);
                setFileBlob(blob);
                if (fileType === 'pdf') {
                    const objectUrl = URL.createObjectURL(blob);
                    setPdfObjectUrl(objectUrl);
                }
            }
            await markAsRead();
        } catch (error) {
            setError(`Ошибка загрузки файла: ${error.message}`);
        } finally {
            setLoading(false);
        }
    };

    const handleDownloadOriginal = () => {
        window.open(`${downloadUrl}`, '_blank');
    };

    const handleDownloadEdited = async () => {
        const text = getCurrentText();
        if (!text) {
            showWarning('Нет текста для скачивания');
            return;
        }

        try {
            await downloadEdited(fileName, baseFileId, text);
            showSuccess('Файл успешно скачан');
        } catch (error) {
            showError(error.message || 'Ошибка при скачивании файла');
        }
    };

    const getCurrentText = () => editorRef.current?.getCurrentText() || '';

    const handleSpellCheck = async (text) => {
        try {
            await checkSpelling(text);
        } catch (error) {
            showError('Ошибка при проверке орфографии');
        }
    };

    const showConfirmDialog = (message, onConfirm, onCancel = null) => {
        setConfirmDialog({
            show: true,
            message,
            onConfirm: () => {
                onConfirm();
                setConfirmDialog(prev => ({ ...prev, show: false }));
            },
            onCancel: () => {
                if (onCancel) onCancel();
                setConfirmDialog(prev => ({ ...prev, show: false }));
            }
        });
    };

    const handleCancelEdit = () => {
        if (hasChanges) {
            showConfirmDialog(
                'У вас есть несохраненные изменения. Закрыть без сохранения?',
                () => {
                    setIsEditing(false);
                    setHasChanges(false);
                }
            );
        } else {
            setIsEditing(false);
        }
    };

    const customToggle = () => {
        if (isEditing && hasChanges) {
            showConfirmDialog(
                'У вас есть несохраненные изменения. Закрыть без сохранения?',
                () => {
                    setModal(false);
                    setIsEditing(false);
                    setHasChanges(false);
                }
            );
        } else {
            if (pdfObjectUrl) {
                URL.revokeObjectURL(pdfObjectUrl);
                setPdfObjectUrl(null);
            }
            setModal(false);
            setIsEditing(false);
        }
    };

    const handleSave = async (text) => {
        setSaving(true);

        try {
            const data = await updateText(inputIdentifier, fileName, version, baseFileId, text);
            setHasChanges(false);

            if (data.isSuccess) {
                // Отправляем уведомления команде о новом обновлении
                await sendFileVersionNotifications('update',
                    {
                        fileName: record.fileName,
                        baseFileId: record.baseFileId,
                        version: data.newVersion
                    },
                    currentUserAccess?.user,
                    id);

                if (onFileUpdated) {
                    await onFileUpdated('newVersion');
                }
                toggleModal(true);
            }

            if (data.isSuccess) {
                showSuccess(`Изменения сохранены в версии v.${data.newVersion}`);
            } else {
                showWarning('Файл не был изменён');
            }
        } catch (error) {
            showError(error.message || 'Ошибка при сохранении', 'error');
        } finally {
            setSaving(false);
        }
    }

    const toggleEditMode = async () => {
        if (!isEditing) {
            if (!fullText && (fileType === 'txt' || fileType === 'md')) {
                await loadFileContent();
            }
            setIsEditing(true);
        } else {
            showConfirmDialog(
                'У вас есть несохраненные изменения. Закрыть без сохранения?',
                () => setIsEditing(false)
            );
        }
    };

    const toggleModal = (force = false) => {
        if (modal && isEditing && !force) {
            showConfirmDialog(
                'У вас есть несохраненные изменения. Закрыть без сохранения?',
                () => {
                    setModal(false);
                    setIsEditing(false);
                }
            );
            return;
        }
        if (!modal && !fullText && !fileBlob) {
            loadFileContent();
        } else if (!modal) {
            if (fileBlob && fileType === 'pdf' && !pdfObjectUrl) {
                const objectUrl = URL.createObjectURL(fileBlob);
                setPdfObjectUrl(objectUrl);
            }
            markAsRead();
        }
        setModal(prev => !prev);
        setIsEditing(false);
    };

    const handleCopyFullTextToClipboard = async () => {
        if (!fullText) {
            showWarning('Нет текста для копирования', 'warning');
            return;
        }

        try {
            await navigator.clipboard.writeText(fullText);
            showSuccess('Текст успешно скопирован в буфер обмена');
            setCopyAllSuccess(true);
            setTimeout(() => setCopyAllSuccess(false), 2000);
        } catch (err) {
            console.error('Failed to copy text: ', err);
            showError('Не удалось скопировать текст');
        }
    };

    // ---------- загрузка новой версии для pdf/docx ----------

    const handleUploadNewVersionClick = () => {
        fileInputRef.current?.click();
    };

    const handleFileSelected = (event) => {
        const file = event.target.files?.[0];
        event.target.value = ''; // даём выбрать тот же файл повторно
        if (!file) return;

        // 1) размер
        if (file.size > MAX_FILE_SIZE) {
            showError(`Файл больше 1 ГБ (${formatFileSize(file.size)}). Загрузка невозможна.`);
            return;
        }

        // 2) расширение
        const actualExt = file.name.split('.').pop()?.toLowerCase();
        if (actualExt !== fileType) {
            showError(`Ожидается файл формата .${fileType}, получен .${actualExt}`);
            return;
        }

        // 3) подтверждение
        showConfirmDialog(
            `Загрузить файл «${file.name}» (${formatFileSize(file.size)}) ` +
            `как новую версию от v.${version}?`,
            () => performUpload(file)
        );
    };

    const performUpload = async (file) => {
        setSaving(true);
        try {
            const data = await uploadNewFileVersion({
                inputIdentifier,
                fileName,
                oldVersion: version,
                baseFileId,
                file
            });

            if (data?.isSuccess) {
                await sendFileVersionNotifications(
                    'update',
                    {
                        fileName: record.fileName,
                        baseFileId: record.baseFileId,
                        version: data.newVersion
                    },
                    currentUserAccess?.user,
                    id
                );

                if (onFileUpdated) {
                    await onFileUpdated('newVersion');
                }

                showSuccess(`Файл загружен как версия v.${data.newVersion}`);
                toggleModal(true);
            } else {
                showWarning('Файл не отличается от исходной версии');
            }
        } catch (err) {
            showError(err.message || 'Ошибка при загрузке новой версии');
        } finally {
            setSaving(false);
        }
    };


    const renderContentView = () => {
        if (loading) return <div className="loading-indicator"><Spinner color="primary" /><p>Загрузка содержимого...</p></div>;
        if (error) return <div className="error-message alert alert-danger">{error}</div>;

        if (fileType === 'pdf') {
            return <PDFViewer fileUrl={pdfObjectUrl} onLoadError={(err) => setError(err.message)} />;
        }
        if (fileType === 'docx') {
            return <DocxViewer blob={fileBlob} onLoadError={setError} />;
        }
        if (fileType === 'md' && !isEditing) {
            return <MarkdownViewer content={fullText} />;
        }
        if (isEditing && (fileType === 'txt' || fileType === 'md')) {
            return (
                <TextEditor
                    ref={editorRef}
                    initialText={fullText}
                    onSpellCheck={handleSpellCheck}
                    onTextChange={setHasChanges}
                    showToolbar={true}
                    showAudioRecorder={true}
                    fontSize={fontSize}
                    setFontSize={setFontSize}
                    lineHeight={lineHeight}
                    setLineHeight={setLineHeight}
                />
            );
        }

        return (
            <TxtViewer
                text={fullText}
                fontSize={fontSize}
                setFontSize={setFontSize}
                lineHeight={lineHeight}
                setLineHeight={setLineHeight}
            />
        );
    };

    const showEditButton = isEditable && !isEditing;
    const showCopyButton = (fileType === 'txt' || fileType === 'md') && !isEditing;

    return (
        <div>
            <Tooltip title="Просмотр результата">
                <IconButton
                    onClick={toggleModal}
                    draggable="false">
                    <AutoStoriesIcon className="view-result-icon" />
                </IconButton>
            </Tooltip>

            {isBinary && (
                <input
                    type="file"
                    ref={fileInputRef}
                    accept={ACCEPT_BY_TYPE[fileType] || ''}
                    style={{ display: 'none' }}
                    onChange={handleFileSelected}
                />
            )}

            <ConfirmDialog
                isOpen={confirmDialog.show}
                message={confirmDialog.message}
                onConfirm={confirmDialog.onConfirm}
                onCancel={confirmDialog.onCancel}
                onClose={() => setConfirmDialog(prev => ({ ...prev, show: false }))}
            />

            <Modal
                isOpen={modal}
                toggle={customToggle}
                onClosed={() => setModal(false)}
                size="xl"
                className="text-preview-modal"
            >
                <ModalHeader toggle={customToggle}>
                    {isEditing ? 'Редактирование текста' : 'Просмотр текста'}: {fileName}
                </ModalHeader>
                <ModalBody className="text-preview-body">
                    <div className="modal-body">
                        {renderContentView()}
                    </div>
                </ModalBody>
                <ModalFooter className="edit-footer">
                    <div className="footer-content">
                        {isEditing ? (
                            <>
                                <Button
                                    className="dt1520-button active-dt1520-button view-button blue-button-with-borders footer-button"
                                    disabled={loading}
                                    onClick={handleDownloadEdited}>
                                    Скачать
                                </Button>
                                <Button
                                    className="dt1520-button active-dt1520-button view-button blue-button-with-borders footer-button"
                                    disabled={loading}
                                    onClick={handleCopyFullTextToClipboard}>
                                    {copyAllSuccess && (
                                        <img src={checkMarkIcon} alt="✓" className='logo-dt1520-button copied' />
                                    )}
                                    Копировать всё
                                </Button>
                                <Button
                                    className="dt1520-button active-dt1520-button view-button green-button footer-button"
                                    onClick={() => handleSave(getCurrentText())}
                                    disabled={loading || saving}>
                                    {saving ? <Spinner size="sm" /> : 'Сохранить'}
                                </Button>
                                <Button
                                    className="dt1520-button active-dt1520-button view-button footer-button default-button"
                                    onClick={handleCancelEdit}>
                                    Отмена
                                </Button>
                            </>
                        ) : (
                            <>
                                <Button
                                    className="dt1520-button active-dt1520-button view-button blue-button-with-borders footer-button"
                                    disabled={loading}
                                    onClick={handleDownloadOriginal}>
                                    Скачать
                                </Button>
                                {showCopyButton && (
                                    <Button className="dt1520-button active-dt1520-button view-button blue-button-with-borders footer-button" onClick={handleCopyFullTextToClipboard} disabled={loading} title="Копировать весь текст в буфер обмена" draggable="false">
                                        {copyAllSuccess && <img src={checkMarkIcon} alt="✓" className='logo-dt1520-button copied' />}
                                        Копировать всё
                                    </Button>
                                )}
                                {showEditButton && (
                                    <Button className="dt1520-button active-dt1520-button view-button blue-button-with-borders footer-button" onClick={toggleEditMode} disabled={loading} draggable="false">
                                        Редактировать текст
                                    </Button>
                                    )}
                                    {canUploadNewVersion && (
                                        <Button
                                            className="dt1520-button active-dt1520-button view-button green-button footer-button"
                                            onClick={handleUploadNewVersionClick}
                                            disabled={loading || saving}
                                            draggable="false">
                                            {saving ? <Spinner size="sm" /> : 'Загрузить новую версию'}
                                        </Button>
                                    )}
                            </>
                        )}
                    </div>
                </ModalFooter>
            </Modal>
        </div>
    );
};